import produce from "immer";
import { useTranslation } from "react-i18next";
import { Checkbox, MenuItem, Typography } from "@material-ui/core";
import SwitchField from "components/Forms/SwitchField";
import { OptionGroup } from "components/Styled";

import { TypeSelectContainer } from "./Styled";
import SelectField from "components/Forms/SelectField";
import { useRuleContext } from "../../context/ruleContext";
import { useFilterCopy } from "./copy";

import { AttachmentType } from "@dashboard-v3/api";
import { FieldGroupProps } from "./types";
import { getContentFilter } from "pages/Rules/Form/context/ruleSelectors";

const attachmentTypes: AttachmentType[] = [
  "IMAGE",
  "AUDIO",
  "TEXT",
  "VIDEO",
  "APPLICATION",
];

type FilterName = "includeAttachmentTypes" | "excludeAttachmentTypes";

const dropDowns = [
  "includeAttachmentTypes",
  "excludeAttachmentTypes",
] as FilterName[];

export default function DropDownFields({
  isEnabled,
  handleEnabledFields,
  updateFilter,
}: FieldGroupProps) {
  const { t } = useTranslation("rules");
  const { state } = useRuleContext();
  const copy = useFilterCopy();
  const filters = getContentFilter(state.rule);

  // Get options based on the opposite filter
  function getOptions(name: FilterName) {
    if (name === "includeAttachmentTypes") {
      return attachmentTypes.filter(
        item => !filters.excludeAttachmentTypes.includes(item)
      );
    }
    if (name === "excludeAttachmentTypes") {
      return attachmentTypes.filter(
        item => !filters.includeAttachmentTypes.includes(item)
      );
    }
  }

  const handleDropdowns = e => {
    const { name, value } = e.target;
    const update = produce(filters, draft => {
      draft[name] = value;
    });
    updateFilter(update);
  };

  function menuTitle(name: FilterName, t: (string) => string) {
    return getOptions(name).length === 0
      ? t(`actionFilters.${name}.menuTitleEmpty`)
      : t(`actionFilters.${name}.menuTitle`);
  }

  return (
    <>
      {dropDowns.map(name => (
        <OptionGroup key={name}>
          <SwitchField
            name={name}
            label={copy.label(name)}
            description={copy.desc(name)}
            onChange={handleEnabledFields}
            value={isEnabled(name)}
            disabled={!!filters?.includeZipWithPassword}
          />
          {isEnabled(name) && (
            <TypeSelectContainer>
              <Typography>{t(`actionFilters.${name}.title`)}</Typography>
              <SelectField
                name={name}
                label={t("actionFilters.attachmentTypeLabel")}
                multiple
                menuTitle={menuTitle(name, t)}
                options={getOptions(name)}
                onChange={handleDropdowns}
                value={filters[name]}
                renderOption={attachmentType => (
                  <MenuItem key={attachmentType} value={attachmentType}>
                    <Checkbox
                      color="primary"
                      checked={(filters[name] as string[]).includes(
                        attachmentType
                      )}
                    />
                    {t(`actionFilters.attachmentType.${attachmentType}`)}
                  </MenuItem>
                )}
                renderValue={values =>
                  listValuesToLabels(values as AttachmentType[], t)
                }
              />
            </TypeSelectContainer>
          )}
        </OptionGroup>
      ))}
    </>
  );
}

function listValuesToLabels(values: AttachmentType[], t: (string) => string) {
  return values
    .map(attachmentType => t(`actionFilters.attachmentType.${attachmentType}`))
    .join(", ");
}
